import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { AMBASSADOR_IMAGE, AMBASSADOR_AVATAR } from '../constants/media';
import { useLanguage } from '../contexts/LanguageContext';
import { LINKS } from '../data/translations';
import { renderRich } from '../utils/richText';
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section className="relative w-full overflow-hidden bg-white" data-id="element-91" data-mp="129h0vnss70df">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(45rem 32rem at 82% 8%, rgba(37,99,235,0.30), transparent 62%), radial-gradient(38rem 30rem at 8% 78%, rgba(59,130,246,0.22), transparent 65%), linear-gradient(180deg, #eff6ff 0%, #ffffff 55%, #f8fafc 100%)'
    }} data-id="element-92" data-mp="128phrcgdev41" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-10 h-[26rem] w-[26rem] rounded-full bg-blue-500/25 blur-3xl" data-id="element-93" data-mp="3d33fj0i70xlt" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-20 bottom-0 h-[20rem] w-[20rem] rounded-full bg-sky-400/20 blur-3xl" data-id="element-94" data-mp="3j897lcz58bpj" />

      <div className="relative mx-auto grid w-full max-w-[1280px] grid-cols-1 items-center gap-14 px-5 py-16 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:py-24" data-id="element-95" data-mp="39acx8ne15ksh">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        ease: 'easeOut'
      }} data-id="element-96" data-mp="3e7l7m1sxskj6">
          <div className="relative max-w-[620px] rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-[0_10px_40px_-20px_rgba(15,23,42,0.25)]" data-id="element-97" data-mp="359j9pv4i2804">
            <div className="flex items-start gap-5" data-id="element-98" data-mp="2viy2jjarfv5l">
              <img src={AMBASSADOR_AVATAR} alt="Joko Prabuwesi, TokoTeta ambassador robot" className="h-[72px] w-[72px] shrink-0 rounded-full object-cover" data-id="element-99" data-mp="2vjpzoyk0a0xz" />
              <div data-id="element-100" data-mp="2ljz5yz39rpyy">
                <p className="flex items-center gap-2 text-[15px] font-bold text-blue-600" data-id="element-101" data-mp="s21hi9lxc9yx">
                  <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden="true" data-id="element-102" data-mp="s3urnyvqxcbo" />
                  {t.hero.quoteLabel}
                </p>
                <p className="mt-2 text-[17px] italic leading-relaxed text-slate-700" data-id="element-103" data-mp="dd5c6kb95b20">
                  {renderRich(t.hero.quote)}
                </p>
              </div>
            </div>
          </div>

          <h1 className="mt-12 text-[clamp(2.75rem,6.4vw,5.25rem)] font-extrabold leading-[1] tracking-[-0.04em] text-slate-900" data-id="element-104" data-mp="161t1qs31amld">
            {t.hero.titleLead}
            <span className="text-blue-600" data-id="element-105" data-mp="11154hoo7p9ip">{t.hero.titleAccent}</span>
          </h1>

          <p className="mt-7 max-w-[560px] text-[18px] leading-relaxed text-slate-600" data-id="element-106" data-mp="x0csinnygqiq">
            {renderRich(t.hero.body)}
          </p>

          <div className="mt-10 grid grid-cols-2 items-stretch gap-3 sm:flex sm:flex-wrap sm:items-center sm:gap-4" data-id="element-107" data-mp="3lqm4435bldx1">
            <a href={LINKS.register} target="_blank" rel="noreferrer" className="group inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3.5 text-center text-[15px] font-bold text-white shadow-lg shadow-blue-600/25 transition-colors hover:bg-blue-700 sm:gap-3 sm:px-9 sm:py-4 sm:text-[17px]" data-id="element-108" data-mp="1x9kw0qpijcjb">
              {t.hero.primaryCta}
              <ArrowRightIcon size={18} className="hidden transition-transform group-hover:translate-x-1 sm:block" data-id="element-109" data-mp="2fyr6jqg5kxu1" />
            </a>
            <a href="#how-it-works" className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3.5 text-center text-[15px] font-bold text-slate-900 shadow-sm transition-colors hover:bg-slate-50 sm:px-9 sm:py-4 sm:text-[17px]" data-id="element-110" data-mp="2ozz426auhun2">
              {t.hero.secondaryCta}
            </a>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut',
        delay: 0.1
      }} className="relative mx-auto w-full max-w-[460px]" data-id="element-111" data-mp="4aupet2693ou">
          <div className="relative overflow-hidden rounded-3xl border-4 border-white bg-slate-100 shadow-2xl" data-id="element-112" data-mp="e7hb1k2bnyrx">
            <img src={AMBASSADOR_IMAGE} alt="TokoTeta ambassador robot presenting in the office" className="aspect-[3/4] w-full object-cover" data-id="element-113" data-mp="98mihqejyvum" />
            <div className="absolute left-4 top-5 max-w-[52%] -rotate-2 rounded-xl bg-slate-900/95 px-4 py-3 shadow-xl ring-2 ring-blue-400/60" data-id="element-114" data-mp="3cpcrme5flp0j">
              <p className="text-[19px] font-extrabold italic leading-none tracking-tight text-white" data-id="element-115" data-mp="3mp3qst4xss6y">
                {t.hero.badgeTitle}
              </p>
              <p className="mt-1 text-[13px] font-bold italic leading-snug text-blue-300" data-id="element-116" data-mp="2yxg4ypmj87bj">
                {t.hero.badgeSubtitle}
              </p>
            </div>
          </div>

          <div className="absolute -bottom-6 right-0 rounded-2xl bg-slate-900 px-6 py-4 shadow-xl sm:-right-6" data-id="element-117" data-mp="19hcd9xc9hb6e">
            <p className="text-[13px] text-slate-400" data-id="element-118" data-mp="1eb6yj57ily7l">
              {t.hero.ambassadorLabel}
            </p>
            <p className="text-[17px] font-bold tracking-tight text-white" data-id="element-119" data-mp="14jkf4ll0wstr">
              {t.hero.ambassadorName}
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}